import { Button, Chip, Grid, IconButton } from "@mui/material";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { JSONObject, TemplateItem } from "@helios/toolbox-types";
import { TemplateBlockRepeat } from "../../../utils/types";
import { useDevice } from "../../../utils/DeviceProvider";
import TemplateElements from "./TemplateElements";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import useEditedStyles from "../../../hooks/useEditedStyles";
import { max } from "lodash-es";

interface TemplateBlockRepeaterProps {
  configKey: string;
  items: TemplateItem[];
  repeat?: TemplateBlockRepeat;
  endUserKeys?: string[];
}

export const TemplateBlockRepeater = ({ repeat, configKey, items, endUserKeys }: TemplateBlockRepeaterProps) => {
  const [repeatIndexes, setRepeatIndexes] = useState<string[]>([]);
  const {
    device: { currentValue },
    setCurrentValue,
  } = useDevice();

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();

  const removeLine = useCallback(
    (index: string) =>
      !repeat
        ? undefined
        : () => {
            currentValue[configKey].items.forEach((item: JSONObject) => {
              const cKey = (item.key ?? item.type) as string;
              if (cKey.startsWith(`${repeat.key}.${index}`)) {
                item.deleted = true;
              }
            });
            // setDeletedIndexes((old) => [...old, index]);
            setCurrentValue(currentValue);
          },
    [configKey, currentValue, repeat, setCurrentValue]
  );

  const restoreLine = useCallback(
    (index: string) =>
      !repeat
        ? undefined
        : () => {
            currentValue[configKey].items.forEach((item: JSONObject) => {
              const cKey = (item.key ?? item.type) as string;
              if (cKey.startsWith(`${repeat.key}.${index}`)) {
                delete item.deleted;
              }
            });
            // setDeletedIndexes((old) => {
            //   console.log(old, [...old.filter((i) => i !== index)]);
            //   return [...old.filter((i) => i !== index)];
            // });
            setCurrentValue(currentValue);
          },
    [configKey, currentValue, repeat, setCurrentValue]
  );

  const newLine = useCallback(() => {
    if (!repeat) {
      return;
    }
    const maxIndex = max(repeatIndexes.map((i) => parseInt(i)));
    const newIndex = `${(maxIndex ?? -1) + 1}`;
    setRepeatIndexes((old) => [...old, newIndex]);
    items.forEach((templateItem) => {
      currentValue[configKey].items.push({ type: templateItem.type, key: `${repeat.key}.${newIndex}.${templateItem.key ?? templateItem.type}`, value: "" });
    });
    setCurrentValue(currentValue);
  }, [configKey, currentValue, items, repeat, repeatIndexes, setCurrentValue]);

  useEffect(() => {
    if (!repeat || !currentValue) {
      setRepeatIndexes([]);
      return;
    }
    const repeatedConfigIndexes = new Set<string>(
      currentValue[configKey].items.filter((item: { key: string }) => item.key?.startsWith(repeat.key)).map((item: { key: string }) => item.key.split(".")[1])
    );

    setRepeatIndexes(Array.from(repeatedConfigIndexes.values()));
  }, [configKey, currentValue, repeat]);

  const deletedIndexes = useMemo(() => {
    if (!repeat) {
      return [];
    }
    const deleted = repeatIndexes.filter((index) =>
      currentValue[configKey].items.some((configItem: { key?: string; type: string; deleted?: boolean }) => {
        const cKey = configItem.key ?? configItem.type;
        return cKey.startsWith(`${repeat.key}.${index}`) && configItem.deleted;
      })
    );
    return deleted;
  }, [configKey, currentValue, repeat, repeatIndexes]);

  if (!repeat) {
    return (
      <TemplateRepeaterProvider blockKey={null} index={null}>
        <TemplateElements configKey={configKey} items={items} endUserKeys={endUserKeys} />
      </TemplateRepeaterProvider>
    );
  }

  return (
    <Grid container spacing={1} sx={{ p: 1 }}>
      {repeatIndexes.map((i, realIndex) => (
        <TemplateRepeaterProvider key={`repeat-${repeat.key}.${i}`} blockKey={repeat.key} index={parseInt(i)}>
          <Grid item xs={12}>
            <TemplateElements
              configKey={configKey}
              items={items}
              disabled={deletedIndexes.includes(i)}
              sx={{ alignItems: "flex-end", p: 0 }}
              postfix={
                realIndex < (repeat.min ?? 1) ? null : !deletedIndexes.includes(i) ? (
                  <Grid item>
                    <IconButton onClick={removeLine(i)}>
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </Grid>
                ) : (
                  <Grid container sx={{ alignItems: "center" }} spacing={0.5}>
                    <Grid item>
                      <Chip label={formatMessage({ defaultMessage: "deleted" })} size="small" className={editedStyle.chip} />
                    </Grid>
                    <Grid item>
                      <Button size="small" onClick={restoreLine(i)} color="inherit">
                        {formatMessage({ defaultMessage: "restore" })}
                      </Button>
                    </Grid>
                  </Grid>
                )
              }
            />
          </Grid>
        </TemplateRepeaterProvider>
      ))}
      <Grid item xs={12}>
        <Button onClick={newLine}>{formatMessage({ defaultMessage: "Add new line" })}</Button>
      </Grid>
    </Grid>
  );
};

const TemplateRepeaterContext = createContext<{ blockKey: string | null; index: number | null } | undefined>(undefined);

function TemplateRepeaterProvider({ children, blockKey, index }: PropsWithChildren<{}> & { blockKey: string | null; index: number | null }) {
  return <TemplateRepeaterContext.Provider value={{ blockKey, index }}>{children}</TemplateRepeaterContext.Provider>;
}

export function useTemplateRepeaterInfo() {
  const templateRepeaterInfo = useContext(TemplateRepeaterContext);
  if (templateRepeaterInfo === undefined) {
    throw new Error("useTemplateRepeaterInfo must be used within a TemplateRepeaterContext");
  }
  return templateRepeaterInfo;
}
